// src/hooks/useLessonProgressUpdater.js
import {useSelector, useDispatch} from 'react-redux';
import {setCourseData} from '../store/course';

// You can pass in a mastery threshold parameter if needed,
// or simply keep it as a constant.
const LESSON_MASTERY_THRESHOLD = 90;

const useLessonProgressUpdater = () => {
  const dispatch = useDispatch();
  const courseData = useSelector(state => state.course.courseData);

  /**
   * updateLessonProgressStates:
   *   This function accepts a lessonId and its new progress (a number between 0 and 100
   *   or a fraction, depending on your design). It then:
   *
   *   - Locates the lesson in the courseData.
   *   - Updates its progress, current slide (if applicable) and mastery flag.
   *   - Recalculates aggregate values for the unit that contains it (like average lesson progress,
   *     count of mastered lessons, etc.).
   *   - Recalculates overall course progress (e.g. course mastery) based on updated unit values.
   *   - Dispatches the updated courseData back to the Redux store.
   *
   * Adjust the calculation of properties (e.g. currentSlide, isMastered) to your app’s logic.
   */
  const updateLessonProgressStates = ({
    lessonId,
    newProgress,
    newLessonMasteryScore,
  }) => {
    if (!courseData || !courseData.units) return;
    // Copy the current course data and update the necessary parts.
    // We'll use map on the units so that only the unit containing the lesson is updated.
    const updatedUnits = courseData.units.map((unit, unitIndex) => {
      // Check if this unit contains the target lesson.
      const lessonExistsInUnit = unit.lessons.some(
        lesson => lesson.lessonId === lessonId,
      );
      // If the unit does not contain the lesson, return it unchanged.
      if (!lessonExistsInUnit) {
        return unit;
      }
      // Update only the lessons in this unit.
      const updatedLessons = unit.lessons.map(lesson => {
        if (lesson.lessonId === lessonId) {
          // Calculate the new values for the lesson.
          // For example, compute currentSlide relative to totalSlides,
          // and set the mastery flag based on the threshold.
          let updatedProgress = Math.max(newProgress, lesson.progress);
          const currentSlide = Math.round(
            (updatedProgress / 100) * lesson.totalSlides,
          );
          let updatedMasteryScore = Math.max(
            newLessonMasteryScore,
            lesson.masteryScore,
          );
          const isMastered =
            (updatedMasteryScore / unit.lessonMasteryScore) * 100 >=
            LESSON_MASTERY_THRESHOLD;

          return {
            ...lesson,
            progress: updatedProgress,
            currentSlide,
            isMastered,
            masteryScore: updatedMasteryScore,
            lesson,
          };
        }
        return lesson;
      });
      // Recalculate unit-level aggregates only for the updated unit.
      const totalLessons = updatedLessons.length;
      const lessonsCompletedPercentage =
        totalLessons > 0
          ? Math.round(
              updatedLessons.reduce((acc, l) => acc + (l.progress || 0), 0) /
                totalLessons,
            )
          : 0;
      const lessonsMastered = updatedLessons.filter(l => l.isMastered).length;
      const unitMasteryPercentage =
        totalLessons > 0
          ? Math.round((lessonsMastered / totalLessons) * 100)
          : 0;
      // This example assumes that "learningContentPercentage" is a property on each lesson.
      const hundredPercentCompletedCount = updatedLessons.filter(
        l => l.progress === 100,
      ).length;

      // Optionally recalculate a flag based on the current study unit.
      const isCurrentlyStudying = courseData.currentUnitIndex === unitIndex;

      return {
        ...unit,
        lessons: updatedLessons,
        totalLessons,
        lessonsCompletedPercentage,
        lessonsMastered,
        unitMasteryPercentage,
        hundredPercentCompletedCount,
        isCurrentlyStudying,
      };
    });

    // Recalculate overall course mastery based on updated unit data.
    const totalUnits = updatedUnits.length;
    const unitsMastered = updatedUnits.filter(
      u => u.unitMasteryPercentage >= LESSON_MASTERY_THRESHOLD,
    ).length;
    const calculatedCourseMastery =
      totalUnits > 0 ? Math.round((unitsMastered / totalUnits) * 100) : 0;

    // Build the updated course data, preserving any other keys that shouldn't change.
    const updatedCourseData = {
      ...courseData,
      units: updatedUnits,
      courseMastery: calculatedCourseMastery,
    };
    // Finally, dispatch the updated courseData to update the redux store.
    dispatch(setCourseData(updatedCourseData));
  };

  return {updateLessonProgressStates};
};

export default useLessonProgressUpdater;
